import { useEffect, useRef } from 'react'

import { scrollNearestScrollable } from '../util/scrollNearestScrollable'

/**
 * Hook that scrolls to the top of the nearest scrollable parent when triggered
 *
 * @param shouldScroll - Whether to trigger the scroll
 * @returns A ref to attach to your container element
 *
 * @example
 * ```tsx
 * const containerRef = useScrollToTop(allCommentsResolved)
 * return <div ref={containerRef}>...</div>
 * ```
 */
export const useScrollToTop = (
  shouldScroll: boolean,
): React.RefObject<HTMLDivElement> => {
  const containerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!shouldScroll || !containerRef.current) return

    let cancelled = false
    let rafId: number | null = null

    // Use requestAnimationFrame for better timing and to avoid layout thrashing
    rafId = requestAnimationFrame(() => {
      if (cancelled || !containerRef.current) return
      scrollNearestScrollable(containerRef.current, 'top')
    })

    return (): void => {
      cancelled = true
      if (rafId !== null) {
        cancelAnimationFrame(rafId)
      }
    }
  }, [shouldScroll])

  return containerRef
}
